import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import formatCurrencyPattern, { formatCurrency } from "common/format-currency-pattern";
import { ADMIN_REPLACEMENT_LEAVE_OPTIONS,getRecommendedReplacementLeaveByTime, calculateOvertimeHours } from "common/overtime";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppCheckbox from "components/app-checkbox";
import AppSelectInput from "components/app-select-input";

export const AppAdminApproveOvertimeModal = (props, ref) => {
	const dispatch = useDispatch();
	const [record, setRecord] = useState({});
	const [visible, setVisible] = useState(false);
	//prettier-ignore
	const initialValues = useMemo(() => ({ includeReplacementLeave: true, replacementLeave: "", includeAllowance: false, allowanceAmount: "" }), []);
	//prettier-ignore
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			replacementLeave: yup.string().when("includeReplacementLeave", { is: true, then: (schema) => schema.required(ERRORS.REQUIRED) }),
			allowanceAmount: yup.string().when("includeAllowance", { is: true, then: (schema) => schema.required(ERRORS.ALLOWANCE_AMOUNT_REQUIRED).test("validAmount", ERRORS.ALLOWANCE_AMOUNT_REQUIRED, (value) => parseFloat((value || "").replace(/,/g, "")) >= 0) }),
		}),
		onSubmit: (values) => {
			if (!values.includeReplacementLeave && !values.includeAllowance) {
				formik.setSubmitting(false);
				return;
			}

			onHandleConfirm(values);
		},
	});

	const overtimeHours = useMemo(() => calculateOvertimeHours(record.startTime, record.endTime), [record.startTime, record.endTime]);

	/* the backend sends no recommendation - the admin may exceed it, unlike the manager */
	const recommended = useMemo(() => getRecommendedReplacementLeaveByTime(record.startTime, record.endTime), [record.startTime, record.endTime]);

	const hasSelection = formik.values.includeReplacementLeave || formik.values.includeAllowance;

	const setFieldValue = useMemo(() => formik.setFieldValue, [formik]);
	const resetForm = useMemo(() => formik.resetForm, [formik]);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	const onHandleShow = useCallback((data) => {
		resetForm();
		setRecord(data || {});
		setVisible(true);
	}, [resetForm]);

	//prettier-ignore
	const onHandleToggleReplacementLeave = useCallback(() => {
		formik.setFieldValue("includeReplacementLeave", !formik.values.includeReplacementLeave);
	}, [formik]);

	//prettier-ignore
	const onHandleToggleAllowance = useCallback(() => {
		formik.setFieldValue("includeAllowance", !formik.values.includeAllowance);
	}, [formik]);

	//prettier-ignore
	const onHandleAllowanceChange = useCallback((event) => {
		const rawValue = event.target.value.replace(/[^0-9.]/g, "");

		formik.setFieldValue("allowanceAmount", rawValue === "" ? "" : formatCurrency(rawValue));
	}, [formik]);

	//prettier-ignore
	const onHandleKeyDown = (event) => {
		const cursorPosition = event.target.selectionEnd;
		const value = event.target.value;

		if (cursorPosition !== value.length) {
			event.target.selectionEnd = value.length;
			event.target.selectionStart = value.length;
			event.preventDefault();
		}

		if (event.key.length === 1 && !event.key.match(/\d/i)) {
			event.preventDefault();
		}
	};

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;

		try {
			const payload = {
				id: record.id,
				includeReplacementLeave: values.includeReplacementLeave,
				replacementLeave: values.includeReplacementLeave ? parseFloat(values.replacementLeave) : null,
				includeAllowance: values.includeAllowance,
				allowanceAmount: values.includeAllowance ? parseFloat(values.allowanceAmount.replace(/,/g, "")) : null,
			};

			await api.post.overtime.approve(payload);
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: "Overtime has been approved successfully" }));
			props.onHandleGetList();
		}
	}, [dispatch, record.id, formik, onHandleDismiss, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	/* prefill with the manager's proposal where present, else the recommendation */
	useEffect(() => {
		if (!visible) return;

		const proposed = record.replacementLeave ?? recommended;

		setFieldValue("replacementLeave", proposed > 0 ? proposed.toString() : "");
	}, [visible, record.replacementLeave, recommended, setFieldValue]);

	return (
		<Modal classes={{ root: "app-admin-approve-overtime-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="admin-approve-overtime-modal" aria-describedby="admin-approve-overtime-modal">
			<div className="admin-approve-overtime-modal">
				<div className="admin-approve-overtime-modal__main">
					<div className="admin-approve-overtime-modal__header">
						<p className="admin-approve-overtime-modal__title">Approve Overtime</p>
						<p className="admin-approve-overtime-modal__description">Confirm the replacement leave and allowance to be granted for this overtime request.</p>
					</div>

					<form className="admin-approve-overtime-modal__body" onSubmit={formik.handleSubmit}>
						<AppInput type="text" label="Employee Name" value={record.employeeName || "-"} disabled />

						<AppInput type="text" label="Overtime Hours" value={overtimeHours ? overtimeHours.toString() : "-"} disabled />

						<AppInput type="text" label="Manager Proposed / Recommended (Days)" value={(record.replacementLeave ?? recommended).toString()} disabled />

						<div className="admin-approve-overtime-modal__checkbox">
							{/* prettier-ignore */}
							<AppCheckbox name="includeReplacementLeave" label="Grant replacement leave" value={formik.values.includeReplacementLeave} disabled={formik.isSubmitting} onClick={onHandleToggleReplacementLeave} />
						</div>

						{/* prettier-ignore */}
						{formik.values.includeReplacementLeave && <AppSelectInput required searchable={false} type="text" name="replacementLeave" label="Replacement Leave (Days)" placeholder="Please Select" options={ADMIN_REPLACEMENT_LEAVE_OPTIONS} value={formik.values.replacementLeave} error={formik.errors.replacementLeave} touched={formik.touched.replacementLeave} disabled={formik.isSubmitting} onChange={formik.handleChange} />}

						<div className="admin-approve-overtime-modal__checkbox">
							{/* prettier-ignore */}
							<AppCheckbox name="includeAllowance" label="Grant allowance" value={formik.values.includeAllowance} disabled={formik.isSubmitting} onClick={onHandleToggleAllowance} />
						</div>

						{/* prettier-ignore */}
						{formik.values.includeAllowance && <AppInput required type="text" name="allowanceAmount" label="Allowance Amount (RM)" placeholder="Enter Amount" value={formik.values.allowanceAmount} error={formik.errors.allowanceAmount} touched={formik.touched.allowanceAmount} disabled={formik.isSubmitting} onChange={onHandleAllowanceChange} onKeyDown={onHandleKeyDown} onFormat={formatCurrencyPattern} maxLength={12} />}

						<div className="admin-approve-overtime-modal__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
							<AppButton type="submit" label="Confirm" disabled={formik.isSubmitting || !hasSelection} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppAdminApproveOvertimeModal));

AppAdminApproveOvertimeModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func,
};
